// src/sectors/pilates/attendance/ui/AttendanceMarkModal/ParticipantAttendanceRow.jsx
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Collapse from '@mui/material/Collapse';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Chip from '@mui/material/Chip';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

import { S, META } from '../utils/attendanceConstants';
import {
  requiresNote,
  isMarkedUiStatus,
  planText,
  remainingText,
  safeUpper,
  shortDateTime,
  packageMonthLabelFrom,
  packagePeriodLabel,
} from '../utils/attendanceUtils';

import { ParticipantAttendanceHistory } from './ParticipantAttendanceHistory';

function statusChipSx(t, status) {
  const m = META[status] || META[S.PENDING];
  const c = t.palette[m.colorKey]?.main || t.palette.info.main;
  return {
    height: 24,
    borderRadius: '999px',
    fontWeight: 900,
    bgcolor: alpha(c, 0.12),
    border: `1px solid ${alpha(c, 0.26)}`,
  };
}

function trISO(iso) {
  if (!iso) return null;
  const s = String(iso);
  const y = s.slice(0, 4);
  const m = s.slice(5, 7);
  const d = s.slice(8, 10);
  if (!y || !m || !d) return s;
  return `${d}.${m}.${y}`;
}

function monthLabelTR(dateISO) {
  if (!dateISO) return null;
  const s = String(dateISO);
  const m = Number(s.slice(5, 7));
  const months = [
    'Ocak',
    'Şubat',
    'Mart',
    'Nisan',
    'Mayıs',
    'Haziran',
    'Temmuz',
    'Ağustos',
    'Eylül',
    'Ekim',
    'Kasım',
    'Aralık',
  ];
  if (!Number.isFinite(m) || m < 1 || m > 12) return null;
  return `${months[m - 1]} Paketi`;
}

function badgeSx(t, colorKey = 'info') {
  const c = t.palette[colorKey]?.main || t.palette.info.main;
  return {
    height: 24,
    borderRadius: '999px',
    fontWeight: 900,
    bgcolor: alpha(c, 0.12),
    border: `1px solid ${alpha(c, 0.22)}`,
  };
}

export function ParticipantAttendanceRow({
  p,
  value,
  open,
  mode, // "DETAIL" | "EDIT"
  readOnly,
  onToggleDetail,
  onOpenEdit,
  onChange,
  onEditRequest, // ✅ edit ikonuna basılınca parent’a haber ver (footer kaydet açsın)
}) {
  const theme = useMuiTheme();

  const status = value?.status || S.PENDING;
  const note = value?.note || '';

  const selectedMeta = META[status] || META[S.PENDING];
  const selectedColor = theme.palette[selectedMeta.colorKey]?.main || theme.palette.info.main;

  const name = p?.fullName || p?.memberNameSnapshot || '—';
  const cur = p?.current || {};

  const pkg = p?.packageUsage || p?.plan || p?.memberPlan || null;

  const planName = pkg?.planName || pkg?.name || p?.planName || p?.packagePlanNameSnapshot || null;

  const planStatus =
    safeUpper(pkg?.status) || safeUpper(pkg?.planStatus) || safeUpper(p?.planStatus) || null;

  const startAt =
    pkg?.startAt || pkg?.plannedStartAt || pkg?.packageStartAt || p?.packageStartAtSnapshot || null;

  const endAt =
    pkg?.endAt ||
    pkg?.estimatedEndAt ||
    pkg?.packageEstimatedEndAt ||
    p?.packageEstimatedEndAtSnapshot ||
    null;

  const monthTag = monthLabelTR(startAt || endAt || p?.sessionDayISO);

  const totals = {
    totalSessions: Number(pkg?.totalSessions ?? 0),
    usedSessions: Number(pkg?.usedSessions ?? 0),
    totalMakeUp: Number(pkg?.totalMakeUp ?? 0),
    usedMakeUp: Number(pkg?.usedMakeUp ?? 0),
  };

  const remainingNow = p?.remainingNow || null;

  const isFinishedByRemaining =
    remainingNow &&
    Number(remainingNow?.normalRemaining ?? 0) === 0 &&
    Number(remainingNow?.makeUpRemaining ?? 0) === 0 &&
    (totals.totalSessions > 0 || totals.totalMakeUp > 0);

  const isFinished =
    planStatus === 'USED' ||
    planStatus === 'CANCELLED' ||
    planStatus === 'ENDED' ||
    planStatus === 'EXPIRED' ||
    isFinishedByRemaining;

  const curLine = (() => {
    const st = safeUpper(cur?.status);
    const who = cur?.markedByName || null;
    const at = shortDateTime(cur?.markedAt);
    if (!who && !at && st === 'PENDING') return 'Yoklama alınmadı';
    const pieces = [];
    if (who) pieces.push(who);
    if (at) pieces.push(at);
    return pieces.length ? `Son işlem: ${pieces.join(' • ')}` : '—';
  })();

  const plan = planText(p);
  const rem = remainingText(p);
  const pkgMonthLabel = packageMonthLabelFrom(p, p?.sessionDayISO);
  const pkgPeriod = packagePeriodLabel(p);

  const isEdit = mode === 'EDIT';
  const isDetail = mode === 'DETAIL';

  const needNote = requiresNote(status);
  const noteOk = !needNote || Boolean(note.trim());

  const toggleGroupSx = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 0.65,
    '& .MuiToggleButton-root': {
      borderRadius: '14px !important',
      textTransform: 'none',
      fontWeight: 900,
      border: `1px solid ${alpha(theme.palette.info.main, 0.16)}`,
      background: theme.palette.mode === 'dark' ? alpha('#fff', 0.035) : alpha('#0b1220', 0.018),
      py: 0.9,
      px: 1.0,
      justifyContent: 'flex-start',
      lineHeight: 1.1,
      minHeight: 40,
      transition: 'transform .12s ease, background .12s ease, border-color .12s ease',
      '&:active': { transform: 'scale(0.99)' },
      '&.Mui-selected': {
        borderColor: alpha(selectedColor, 0.7),
        background: alpha(selectedColor, theme.palette.mode === 'dark' ? 0.18 : 0.12),
        boxShadow: `inset 0 0 0 1px ${alpha(selectedColor, 0.2)}`,
      },
      '&.Mui-selected:hover': {
        background: alpha(selectedColor, theme.palette.mode === 'dark' ? 0.22 : 0.14),
      },
    },
  };

  // (şu an kullanılmıyor ama ileride lazımsa kalsın diye dokunmadım)
  const planSessionList = useMemo(() => {
    const xs =
      (Array.isArray(p?.planSessions) && p.planSessions) ||
      (Array.isArray(p?.packageSessions) && p.packageSessions) ||
      null;
    if (!xs) return [];
    return xs.filter(Boolean).slice(0, 12);
  }, [p]);

  const handleHeaderClick = () => {
    if (readOnly) return onToggleDetail();
    if (isMarkedUiStatus(status)) onToggleDetail();
    else onOpenEdit();
  };

  return (
    <MDBox
      sx={(t) => ({
        borderRadius: '16px',
        border: `1px solid ${alpha(t.palette.info.main, 0.14)}`,
        background:
          t.palette.mode === 'dark'
            ? 'linear-gradient(180deg, rgba(255,255,255,.03), rgba(255,255,255,0))'
            : 'linear-gradient(180deg, rgba(2,6,23,.018), rgba(2,6,23,0))',
        overflow: 'hidden',
      })}
    >
      {/* HEADER */}
      <MDBox
        onClick={handleHeaderClick}
        sx={(t) => ({
          px: 1.0,
          py: 0.9,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          cursor: 'pointer',
          '&:active': {
            background: alpha(t.palette.info.main, t.palette.mode === 'dark' ? 0.08 : 0.05),
          },
        })}
      >
        <MDBox sx={{ minWidth: 0, display: 'grid', gap: 0.25 }}>
          <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 0.7, minWidth: 0 }}>
            <MDTypography variant="button" fontWeight="bold" noWrap sx={{ lineHeight: 1.1 }}>
              {name}
            </MDTypography>

            {monthTag ? (
              <Chip size="small" label={monthTag} sx={(t) => badgeSx(t, 'info')} />
            ) : null}
            {isFinished ? (
              <Chip size="small" label="Paket Bitti" sx={(t) => badgeSx(t, 'error')} />
            ) : null}
          </MDBox>

          <MDTypography variant="caption" sx={{ opacity: 0.84 }} noWrap>
            <b>{planName || plan || 'Paket'}</b>
            {pkgMonthLabel ? ` — ${pkgMonthLabel}` : ''}
          </MDTypography>

          <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 0.8, minWidth: 0 }}>
            <MDTypography variant="caption" sx={{ opacity: 0.7 }} noWrap>
              {rem || '—'}
              {pkgPeriod ? ` • ${pkgPeriod}` : ''}
            </MDTypography>

            {/* ✅ geçmiş yoklama pill'leri modüle taşındı */}
            <ParticipantAttendanceHistory p={p} variant="header" />
          </MDBox>

          <MDTypography variant="caption" sx={{ opacity: 0.62 }} noWrap>
            {curLine}
          </MDTypography>
        </MDBox>

        <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
          <Chip size="small" label={selectedMeta.label} sx={(t) => statusChipSx(t, status)} />

          {needNote && !noteOk ? (
            <Tooltip title="Açıklama zorunlu" arrow>
              <Icon sx={{ color: (t) => t.palette.error.main, fontSize: 18 }}>error</Icon>
            </Tooltip>
          ) : null}

          {/* ✅ HER ZAMAN SADECE ICON */}
          {!readOnly ? (
            <Tooltip title="Düzenle" arrow>
              <IconButton
                size="small"
                sx={(t) => ({
                  width: 38,
                  height: 38,
                  borderRadius: '14px',
                  border: `1px solid ${alpha(t.palette.info.main, 0.16)}`,
                  bgcolor: isEdit ? alpha(selectedColor, 0.1) : 'transparent',
                })}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!open) onToggleDetail();
                  onOpenEdit();
                  onEditRequest?.(p);
                }}
              >
                <Icon fontSize="small" sx={{ color: alpha(selectedColor, 0.9) }}>
                  edit
                </Icon>
              </IconButton>
            </Tooltip>
          ) : null}
        </MDBox>
      </MDBox>

      <Collapse in={open} timeout={180} unmountOnExit>
        <Divider sx={{ opacity: 1, borderColor: alpha(selectedColor, 0.22) }} />

        {/* DETAIL */}
        {isDetail ? (
          <MDBox sx={{ px: 1.0, py: 0.9, display: 'grid', gap: 0.8 }}>
            <MDTypography variant="caption" sx={{ opacity: 0.9, fontWeight: 900 }}>
              Yoklama Detayı
            </MDTypography>

            {planName || totals.totalSessions || totals.totalMakeUp || startAt || endAt ? (
              <MDBox
                sx={{
                  p: 0.9,
                  borderRadius: '16px',
                  border: `1px solid ${alpha(theme.palette.info.main, 0.14)}`,
                  bgcolor:
                    theme.palette.mode === 'dark' ? alpha('#fff', 0.03) : alpha('#0b1220', 0.018),
                  display: 'grid',
                  gap: 0.6,
                }}
              >
                <MDBox sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                  <MDBox sx={{ minWidth: 0 }}>
                    <MDTypography variant="button" fontWeight="bold" noWrap>
                      {planName || 'Paket'}
                    </MDTypography>
                    <MDTypography variant="caption" sx={{ opacity: 0.72, display: 'block' }} noWrap>
                      {monthTag || 'Paket'}
                      {startAt ? ` • ${trISO(startAt)}` : ''}
                      {endAt ? ` → ${trISO(endAt)}` : ''}
                    </MDTypography>
                  </MDBox>

                  {remainingNow ? (
                    <MDBox sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                      <Chip
                        size="small"
                        label={`Kalan: ${Number(remainingNow?.normalRemaining ?? 0)}`}
                        sx={(t) =>
                          badgeSx(
                            t,
                            Number(remainingNow?.normalRemaining ?? 0) === 0 ? 'error' : 'success'
                          )
                        }
                      />
                      <Chip
                        size="small"
                        label={`Telafi: ${Number(remainingNow?.makeUpRemaining ?? 0)}`}
                        sx={(t) =>
                          badgeSx(
                            t,
                            Number(remainingNow?.makeUpRemaining ?? 0) === 0 ? 'error' : 'success'
                          )
                        }
                      />
                    </MDBox>
                  ) : null}
                </MDBox>

                <MDBox sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                  <Chip
                    size="small"
                    label={`Seans: ${totals.usedSessions}/${totals.totalSessions}`}
                    sx={(t) => badgeSx(t, 'info')}
                  />
                  <Chip
                    size="small"
                    label={`Telafi: ${totals.usedMakeUp}/${totals.totalMakeUp}`}
                    sx={(t) => badgeSx(t, 'info')}
                  />
                </MDBox>

                {/* ✅ paket geçmişi modüle taşındı */}
                <ParticipantAttendanceHistory p={p} variant="detail" />
              </MDBox>
            ) : null}

            <MDBox sx={{ display: 'grid', gap: 0.35 }}>
              <MDTypography variant="caption" sx={{ opacity: 0.85 }}>
                Durum: <b>{selectedMeta.label}</b>
              </MDTypography>

              {note?.trim() ? (
                <MDTypography variant="caption" sx={{ opacity: 0.85 }}>
                  Açıklama: <b>{note}</b>
                </MDTypography>
              ) : (
                <MDTypography variant="caption" sx={{ opacity: 0.7 }}>
                  Açıklama yok.
                </MDTypography>
              )}

              {cur?.markedByName || cur?.markedAt ? (
                <MDTypography variant="caption" sx={{ opacity: 0.65 }}>
                  Son işlem: {cur?.markedByName || '—'} • {shortDateTime(cur?.markedAt) || '—'}
                </MDTypography>
              ) : null}
            </MDBox>
          </MDBox>
        ) : null}

        {/* EDIT (butonsuz) */}
        {isEdit && !readOnly ? (
          <MDBox sx={{ px: 1.0, py: 0.95, display: 'grid', gap: 0.85 }}>
            <MDTypography variant="caption" sx={{ opacity: 0.9, fontWeight: 900 }}>
              Durum
            </MDTypography>

            <ToggleButtonGroup
              exclusive
              value={status}
              onChange={(_, v) => v && onChange({ ...value, status: v })}
              fullWidth
              sx={toggleGroupSx}
            >
              {[S.PENDING, S.PRESENT, S.ABSENT, S.CANCELLED, S.BURN].map((k) => {
                const m = META[k];
                const c = theme.palette[m.colorKey]?.main || theme.palette.info.main;
                const isSelected = status === k;

                return (
                  <Tooltip key={k} title={m.help} arrow placement="top">
                    <ToggleButton value={k}>
                      <Icon sx={{ fontSize: 18, mr: 0.7, color: isSelected ? c : alpha(c, 0.85) }}>
                        {m.icon}
                      </Icon>
                      <MDTypography
                        variant="button"
                        sx={{
                          fontWeight: 900,
                          fontSize: 13,
                          color: isSelected ? c : theme.palette.text.primary,
                        }}
                      >
                        {m.label}
                      </MDTypography>
                    </ToggleButton>
                  </Tooltip>
                );
              })}
            </ToggleButtonGroup>

            <MDBox>
              <MDBox
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 0.55,
                }}
              >
                <MDTypography variant="caption" sx={{ opacity: 0.88, fontWeight: 900 }}>
                  Açıklama {requiresNote(status) ? '(zorunlu)' : '(opsiyonel)'}
                </MDTypography>

                {requiresNote(status) && !note.trim() ? (
                  <Chip
                    size="small"
                    label="Gerekli"
                    sx={{
                      height: 22,
                      borderRadius: '999px',
                      fontWeight: 900,
                      bgcolor: alpha(theme.palette.error.main, 0.12),
                      border: `1px solid ${alpha(theme.palette.error.main, 0.22)}`,
                    }}
                  />
                ) : null}
              </MDBox>

              <TextField
                value={note}
                onChange={(e) => onChange({ ...value, note: e.target.value })}
                placeholder="Açıklama / not..."
                fullWidth
                multiline
                minRows={2}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '16px',
                    borderColor: alpha(selectedColor, 0.2),
                    background:
                      theme.palette.mode === 'dark'
                        ? alpha('#fff', 0.05)
                        : alpha(selectedColor, 0.035),
                    '&:hover fieldset': { borderColor: alpha(selectedColor, 0.32) },
                    '&.Mui-focused fieldset': { borderColor: alpha(selectedColor, 0.58) },
                  },
                }}
              />
            </MDBox>
          </MDBox>
        ) : null}
      </Collapse>
    </MDBox>
  );
}

ParticipantAttendanceRow.propTypes = {
  p: PropTypes.object.isRequired,
  value: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(['DETAIL', 'EDIT']).isRequired,
  readOnly: PropTypes.bool,
  onToggleDetail: PropTypes.func.isRequired,
  onOpenEdit: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onEditRequest: PropTypes.func,
};

ParticipantAttendanceRow.defaultProps = {
  readOnly: false,
  onEditRequest: null,
};
